import axios, { AxiosError } from 'axios'

/**
 * Shared axios instance.
 *
 * @remarks
 * `withCredentials` is required — the session is an HTTP-only cookie, never a
 * bearer token. The default base URL is relative so the Vite dev proxy and the
 * nginx production proxy both work without extra configuration.
 */
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api',
  timeout: 20000,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
})

export default api

/** The origin used for full-page redirects such as the Discord OAuth handshake. */
export function apiBaseUrl(): string {
  return import.meta.env.VITE_API_URL || '/api'
}

/**
 * Extracts a user-presentable message from a failed request.
 *
 * @param error - Anything thrown by axios or application code.
 * @param fallback - Message used when the server sent no `error` field.
 *
 * @example
 * ```typescript
 * try {
 *   await api.post('/upload', form)
 * } catch (error) {
 *   setMessage(errorMessage(error, 'Upload failed.'))
 * }
 * ```
 */
export function errorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  const axiosError = error as AxiosError<{ error?: string }>
  return axiosError?.response?.data?.error || (error as Error)?.message || fallback
}
